/**
 * Client-Side Telemetry Service
 *
 * Collects WebRTC telemetry (events + stats) from the BBB whiteboard client
 * and sends them to the LessonWise API for diagnostic purposes.
 *
 * Features:
 * - Buffer + flush pattern: events are batched and sent every flushIntervalMs
 * - Stats collection: polls RTCPeerConnection.getStats() every statsIntervalMs
 * - PII stripping: removes fullname/email/name from payloads
 * - Retry on flush failure: re-adds failed events to buffer (up to maxBufferSize)
 */

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — Auth is an untyped JS singleton in BBB
import Auth from '/imports/ui/services/auth';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — getLwBackendApiUrl is in a JS file without type declarations
import { getLwBackendApiUrl } from '/imports/ui/components/app/service';
import logger from '/imports/startup/client/logger';

// ============================================================================
// TYPES
// ============================================================================

interface TelemetryEvent {
  category: string;
  type: string;
  level: 'info' | 'warn' | 'error';
  payload?: Record<string, unknown>;
  userRole?: string;
  clientTimestamp?: string;
}

interface TelemetryConfig {
  enabled: boolean;
  statsIntervalMs: number;
  flushIntervalMs: number;
  maxBufferSize: number;
}

interface StatsSnapshot {
  packetsReceived?: number;
  packetsLost?: number;
  bytesReceived?: number;
  jitter?: number;
  roundTripTime?: number;
  packetLossRate?: number;
  mosScore?: number;
  iceCandidateType?: string;
  iceTransportProtocol?: string;
  connectionState?: string;
  availableOutgoingBitrate?: number;
  availableIncomingBitrate?: number;
}

// RTCStatsReport entries lack full typing in lib.dom.d.ts — define the fields we access
interface RTCCandidatePairStats {
  id: string;
  type: 'candidate-pair';
  nominated?: boolean;
  selected?: boolean;
  localCandidateId?: string;
  currentRoundTripTime?: number;
  availableOutgoingBitrate?: number;
  availableIncomingBitrate?: number;
}

interface RTCLocalCandidateStats {
  id: string;
  type: 'local-candidate';
  candidateType?: string;
  protocol?: string;
}

interface RTCTransportStats {
  id: string;
  type: 'transport';
  selectedCandidatePairId?: string;
}

// ============================================================================
// PII STRIPPING
// ============================================================================

const PII_FIELDS = new Set(['fullname', 'email', 'name', 'fullName', 'userName']);

function stripPII(obj: unknown): unknown {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripPII);

  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (PII_FIELDS.has(key)) continue;
    cleaned[key] = typeof value === 'object' ? stripPII(value) : value;
  }
  return cleaned;
}

// ============================================================================
// MOS CALCULATION (matches existing stats.js calculateMOS)
// ============================================================================

function calculateMOS(rate: number): number {
  return 1 + 0.035 * rate + 0.000007 * rate * (rate - 60) * (100 - rate);
}

// ============================================================================
// TELEMETRY SERVICE
// ============================================================================

class TelemetryService {
  private buffer: TelemetryEvent[] = [];
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private statsIntervals: Map<string, ReturnType<typeof setInterval>> = new Map();
  private config: TelemetryConfig = {
    enabled: false,
    statsIntervalMs: 10000,
    flushIntervalMs: 10000,
    maxBufferSize: 20,
  };
  private initialized = false;
  private clientEnv: { browserName: string; browserVersion: string; osPlatform: string } | null = null;

  init(config?: Partial<TelemetryConfig>) {
    if (this.initialized) return;

    if (config) {
      this.config = { ...this.config, ...config };
    }

    if (!this.config.enabled) {
      logger.debug({ logCode: 'telemetry_disabled' }, 'Telemetry service disabled');
      return;
    }

    this.clientEnv = this.detectClientEnv();
    this.flushInterval = setInterval(() => this.flush(), this.config.flushIntervalMs);
    this.initialized = true;

    logger.info({ logCode: 'telemetry_initialized' }, 'Telemetry service initialized');
  }

  private detectClientEnv() {
    const ua = navigator.userAgent;
    let browserName = 'unknown';
    let browserVersion = 'unknown';

    if (ua.includes('Firefox/')) {
      browserName = 'Firefox';
      browserVersion = ua.split('Firefox/')[1]?.split(' ')[0] || 'unknown';
    } else if (ua.includes('Edg/')) {
      browserName = 'Edge';
      browserVersion = ua.split('Edg/')[1]?.split(' ')[0] || 'unknown';
    } else if (ua.includes('Chrome/')) {
      browserName = 'Chrome';
      browserVersion = ua.split('Chrome/')[1]?.split(' ')[0] || 'unknown';
    } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
      browserName = 'Safari';
      browserVersion = ua.split('Version/')[1]?.split(' ')[0] || 'unknown';
    }

    let osPlatform = 'unknown';
    if (ua.includes('Windows')) osPlatform = 'Windows';
    else if (ua.includes('Mac OS')) osPlatform = 'macOS';
    else if (ua.includes('Linux')) osPlatform = 'Linux';
    else if (ua.includes('Android')) osPlatform = 'Android';
    else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) osPlatform = 'iOS';

    return { browserName, browserVersion, osPlatform };
  }

  // ============================================================================
  // EVENT TRACKING
  // ============================================================================

  trackEvent(
    category: string,
    type: string,
    level: 'info' | 'warn' | 'error',
    payload?: Record<string, unknown>,
  ) {
    if (!this.config.enabled || !this.initialized) return;

    const event: TelemetryEvent = {
      category,
      type,
      level,
      payload: stripPII(payload || {}) as Record<string, unknown>,
      clientTimestamp: new Date().toISOString(),
    };

    this.buffer.push(event);

    // Auto-flush if buffer is full
    if (this.buffer.length >= this.config.maxBufferSize) {
      this.flush();
    }
  }

  trackAudioEvent(type: string, level: 'info' | 'warn' | 'error', payload?: Record<string, unknown>) {
    this.trackEvent('audio', type, level, payload);
  }

  trackVideoEvent(type: string, level: 'info' | 'warn' | 'error', payload?: Record<string, unknown>) {
    this.trackEvent('video', type, level, payload);
  }

  trackConnectionEvent(type: string, level: 'info' | 'warn' | 'error', payload?: Record<string, unknown>) {
    this.trackEvent('connection', type, level, payload);
  }

  trackScreenshareEvent(type: string, level: 'info' | 'warn' | 'error', payload?: Record<string, unknown>) {
    this.trackEvent('screenshare', type, level, payload);
  }

  // ============================================================================
  // STATS COLLECTION
  // ============================================================================

  startStatsCollection(peerConnection: RTCPeerConnection, mediaType: 'audio' | 'video' | 'screenshare') {
    if (!this.config.enabled || !this.initialized) return;

    // Stop any existing collection for this media type
    this.stopStatsCollection(mediaType);

    const interval = setInterval(async () => {
      try {
        const rtcStats = await peerConnection.getStats();
        const snapshot = this.extractStatsSnapshot(rtcStats, peerConnection);
        if (snapshot) {
          this.sendStats(mediaType, snapshot);
        }
      } catch {
        // Peer connection may have been closed — stop collecting
        this.stopStatsCollection(mediaType);
      }
    }, this.config.statsIntervalMs);

    this.statsIntervals.set(mediaType, interval);
  }

  stopStatsCollection(mediaType: string) {
    const interval = this.statsIntervals.get(mediaType);
    if (interval) {
      clearInterval(interval);
      this.statsIntervals.delete(mediaType);
    }
  }

  private extractStatsSnapshot(
    stats: RTCStatsReport,
    peerConnection: RTCPeerConnection,
  ): StatsSnapshot | null {
    let inboundRtp: RTCInboundRtpStreamStats | null = null;
    let candidatePair: RTCCandidatePairStats | null = null;
    let localCandidate: RTCLocalCandidateStats | null = null;
    let transport: RTCTransportStats | null = null;

    stats.forEach((report) => {
      switch (report.type) {
        case 'inbound-rtp':
          if (!inboundRtp) inboundRtp = report as RTCInboundRtpStreamStats;
          break;
        case 'candidate-pair':
          if ((report as RTCCandidatePairStats).nominated || (report as RTCCandidatePairStats).selected) {
            candidatePair = report as RTCCandidatePairStats;
          }
          break;
        case 'local-candidate':
          if (!localCandidate) localCandidate = report as RTCLocalCandidateStats;
          break;
        case 'transport':
          transport = report as RTCTransportStats;
          break;
      }
    });

    // If no transport/candidate-pair found by nomination, find via transport reference
    if (!candidatePair && transport?.selectedCandidatePairId) {
      stats.forEach((report) => {
        if (report.type === 'candidate-pair' && report.id === transport!.selectedCandidatePairId) {
          candidatePair = report as RTCCandidatePairStats;
        }
      });
    }

    // Look up the actual local candidate from the candidate pair
    if (candidatePair?.localCandidateId) {
      stats.forEach((report) => {
        if (report.id === candidatePair!.localCandidateId) {
          localCandidate = report as RTCLocalCandidateStats;
        }
      });
    }

    if (!inboundRtp) return null;

    const packetsReceived = inboundRtp.packetsReceived ?? 0;
    const packetsLost = inboundRtp.packetsLost ?? 0;
    const bytesReceived = (inboundRtp as Record<string, unknown>).bytesReceived as number | undefined;
    const rate = packetsReceived > 0
      ? ((packetsReceived - packetsLost) / packetsReceived) * 100
      : 100;
    const packetLossRate = 1 - rate / 100;

    return {
      packetsReceived,
      packetsLost,
      bytesReceived: bytesReceived ?? undefined,
      jitter: inboundRtp.jitter ?? null,
      roundTripTime: candidatePair?.currentRoundTripTime ?? null,
      packetLossRate,
      mosScore: calculateMOS(Math.max(0, Math.min(100, rate))),
      iceCandidateType: localCandidate?.candidateType ?? null,
      iceTransportProtocol: localCandidate?.protocol ?? null,
      connectionState: peerConnection.connectionState ?? null,
      availableOutgoingBitrate: candidatePair?.availableOutgoingBitrate ?? null,
      availableIncomingBitrate: candidatePair?.availableIncomingBitrate ?? null,
    };
  }

  // ============================================================================
  // NETWORK
  // ============================================================================

  private getApiUrl(): string {
    return getLwBackendApiUrl();
  }

  private getInternalMeetingId(): string | null {
    return (Auth as Record<string, string>).meetingID || null;
  }

  private getSessionToken(): string | null {
    return (Auth as Record<string, string>)._externUserID
      || (Auth as Record<string, string>).userID
      || null;
  }

  private async flush() {
    if (this.buffer.length === 0) return;

    const internalMeetingID = this.getInternalMeetingId();
    const sessionToken = this.getSessionToken();

    if (!internalMeetingID || !sessionToken) return;

    // Take all events from buffer
    const events = this.buffer.splice(0, this.buffer.length);

    try {
      const response = await fetch(`${this.getApiUrl()}/wb/telemetry/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          internalMeetingID,
          externalUserID: sessionToken,
          events,
          clientEnv: this.clientEnv,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      // Re-add failed events back to buffer (up to max)
      const remaining = this.config.maxBufferSize - this.buffer.length;
      if (remaining > 0) {
        this.buffer.unshift(...events.slice(0, remaining));
      }

      logger.debug({
        logCode: 'telemetry_flush_failed',
        extraInfo: { errorMessage: (error as Error).message },
      }, 'Telemetry flush failed, events re-queued');
    }
  }

  private async sendStats(mediaType: string, stats: StatsSnapshot) {
    const internalMeetingID = this.getInternalMeetingId();
    const sessionToken = this.getSessionToken();

    if (!internalMeetingID || !sessionToken) return;

    try {
      await fetch(`${this.getApiUrl()}/wb/telemetry/stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          internalMeetingID,
          externalUserID: sessionToken,
          mediaType,
          stats,
          clientTimestamp: new Date().toISOString(),
        }),
      });
    } catch {
      // Stats are best-effort; don't retry
    }
  }

  // ============================================================================
  // LIFECYCLE
  // ============================================================================

  destroy() {
    // Flush remaining events
    this.flush();

    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }

    // Stop all stats collection
    for (const [mediaType] of this.statsIntervals) {
      this.stopStatsCollection(mediaType);
    }

    this.initialized = false;
  }
}

const telemetryService = new TelemetryService();
export default telemetryService;
